import React, { useState, useEffect } from "react";
import { Button, Typography, Card } from "@material-tailwind/react";
import AppImages from "../../../../assets/AppImages";
import { Check } from "iconoir-react";

import { InspectionSection, Vehicle } from "../../../../redux/vehicles/actions";

type TabInspectionCardProps = {
    section: InspectionSection;
    vehicle: Vehicle;
    onClose: () => void;
    onSave: (answers: { [key: string]: string }) => void;
};

export const TabInspectionCard: React.FC<TabInspectionCardProps> = ({
    section,
    vehicle,
    onClose,
    onSave,
}) => {
    const [currentInspection, setCurrentInspection] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<any>({});

    useEffect(() => {
        if (vehicle?.inspection_results && section?.id) {
            if (vehicle.inspection_results?.['sections'] && vehicle.inspection_results?.['sections']?.[section.id]) {
                setAnswers(vehicle.inspection_results['sections'][section.id]);
            }
        }
    }, [vehicle, section]);

    const nextQuestion = () => {
        if (currentQuestion < section.inspections[currentInspection].questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        } else if (currentInspection < section.inspections.length - 1) {
            setCurrentInspection(currentInspection + 1);
            setCurrentQuestion(0);
        } else {
            onSave(answers);
        }
    }

    return (
        <React.Fragment>
            <Card className="p-8 gap-y-5 flex flex-col">
                <div className="flex justify-between">
                    <div className="flex items-center gap-x-3">
                        <div className="rounded-md p-2 bg-[#1E293B]">
                            <AppImages.iconQuestionMark className="w-4 h-4" />
                        </div>
                        <div className="flex flex-col gap-y-1">
                            <Typography
                                color="primary"
                                className="text-lg items-center text-foreground flex gap-x-3 font-bold"
                            >
                                {currentInspection + 1}/{section.inspections.length} -{" "}
                                {section.inspections[currentInspection].title}
                            </Typography>
                            <Typography
                                type="small"
                                className="items-center text-[#64748B] flex gap-x-3"
                            >
                                {section.inspections[currentInspection].subtitle}
                            </Typography>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <Typography
                            type="small"
                            color="default"
                            className="font-md text-foreground"
                        >
                            {currentQuestion + 1}/
                            {section.inspections[currentInspection].questions.length}
                        </Typography>
                    </div>
                </div>
                <div className="h-[2px] grow bg-gray-200" />

                <div className="flex flex-col justify-between">
                    <Typography className="text-black text-2xl font-semibold text-center">
                        {section.inspections[currentInspection].questions[currentQuestion].title}
                    </Typography>

                    <div className="flex flex-col md:flex-row gap-5 mx-auto my-10">
                        {section.inspections[currentInspection].questions[
                            currentQuestion
                        ].options.map((option, index) => (
                            <Button
                                variant="outline"
                                color="secondary"
                                key={index}
                                className={`py-3 px-5 rounded-md flex items-center gap-x-2 ${
                                    answers[
                                        section.inspections[currentInspection].questions[
                                            currentQuestion
                                        ].id
                                    ] === option.id
                                        ? "border-green-500"
                                        : ""
                                }`}
                                onClick={() => {
                                    setAnswers({
                                        ...answers,
                                        [section.inspections[currentInspection].questions[
                                            currentQuestion
                                        ].id]: option.id,
                                    });
                                    nextQuestion();
                                }}
                            >
                                {answers[
                                    section.inspections[currentInspection].questions[
                                        currentQuestion
                                    ].id
                                ] === option.id && <Check className="w-5 h-5 text-green-500" />}
                                <Typography className="text-foreground">{option.title}</Typography>
                            </Button>
                        ))}
                    </div>

                    <div className="flex justify-between">
                        <div className="flex gap-x-3">
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-x-3"
                                onClick={onClose}
                            >
                                <Typography type="small" className="block text-foreground">
                                    Cancel
                                </Typography>
                            </Button>
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-x-3"
                                onClick={() => onSave(answers)}
                            >
                                <Typography type="small" className="block text-foreground">
                                    Save
                                </Typography>
                            </Button>
                        </div>

                        <div className="flex gap-x-3">
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-x-3"
                                onClick={() => {
                                    if (currentQuestion > 0) {
                                        setCurrentQuestion(currentQuestion - 1);
                                    } else if (currentInspection > 0) {
                                        setCurrentInspection((prev) => {
                                            setCurrentQuestion(
                                                section.inspections[prev - 1].questions.length - 1
                                            );
                                            return prev - 1;
                                        });
                                    }
                                }}
                            >
                                <AppImages.iconArrowLeft className="w-4 h-4" />
                                <Typography type="small" className="hidden md:block text-foreground">
                                    Previous
                                </Typography>
                            </Button>
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-x-3"
                                onClick={nextQuestion}
                            >
                                <Typography type="small" className="hidden md:block text-foreground">
                                    Next
                                </Typography>
                                <AppImages.iconArrowLeft className="rotate-180 w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        </React.Fragment>
    );
};
